import {
  PaymentStatus,
  PaymentStatusColor,
  PaymentStatusText,
} from '../../../../utils/paymentStatus';

/**
 * Status de pagamento da recarga cross, no mesmo rótulo e na mesma cor do resto do
 * dashboard (MV2-1717).
 *
 * Rótulo e cor saem dos mapas do financeiro em vez de uma tabela própria: é o mesmo
 * dado, na mesma coluna conceitual, e uma cópia empataria hoje para divergir na
 * primeira vez que um status novo entrasse lá e não aqui.
 *
 * O caso das maiúsculas é real e não defensivo: a plataforma grava o status em
 * minúsculas, e o ETL do cross (V39) grava `UPPER(tpp.payment_status)`. Os dois
 * chegam a esta tela — o cross pela listagem, a origem em qualquer reprocessamento
 * — então a comparação é insensível ao caso.
 */

const STATUS_BY_VALUE = new Map<string, PaymentStatus>(
  Object.values(PaymentStatus).map((status) => [status.toLowerCase(), status]),
);

export type PaymentStatusView = {
  label: string;

  /**
   * Cor de Tag do antd, ou `undefined` para a Tag neutra.
   *
   * Nem todo status do financeiro tem cor lá: cancelado e estornado saem na Tag
   * padrão. Aqui saem iguais, porque o pedido era corresponder às outras telas — e
   * inventar uma cor só neste módulo desfaria exatamente isso.
   */
  color: string | undefined;
};

/**
 * Devolve `null` quando não há pagamento registrado.
 *
 * Nulo em vez do travessão porque a diferença importa na tela: com status, a coluna
 * mostra Tag — inclusive a neutra, como o financeiro faz nos status sem cor; sem
 * status, mostra o travessão solto, como as outras colunas vazias desta tabela.
 * Devolvendo o travessão aqui, quem chama não teria como distinguir os dois casos.
 */
export const paymentStatusView = (raw: string | null | undefined): PaymentStatusView | null => {
  if (!raw) {
    return null;
  }

  const known = STATUS_BY_VALUE.get(raw.toLowerCase());

  if (!known) {
    // Status que o gateway passou a devolver e a tela ainda não nomeia. Sai cru:
    // quem confere faturamento precisa ver que existe.
    return { label: raw, color: undefined };
  }

  return {
    label: (PaymentStatusText as Record<string, string>)[known],
    color: (PaymentStatusColor as Record<string, string>)[known],
  };
};
